import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";
import { Loader2, CheckCircle, Eye, EyeOff } from "lucide-react";
import logoImg from "@/assets/images/logo.png";

export default function RedefinirSenha() {
  const [, setLocation] = useLocation();
  const [pronto, setPronto] = useState(false);
  const [senha, setSenha] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [error, setError] = useState("");
  const [linkInvalido, setLinkInvalido] = useState(false);

  useEffect(() => {
    // Link expirado/inválido: o Supabase devolve error/error_description no hash ou query.
    const params = new URLSearchParams(
      `${window.location.search.replace(/^\?/, "")}&${window.location.hash.replace(/^#/, "")}`,
    );
    if (params.get("error") || params.get("error_description")) {
      setLinkInvalido(true);
      return;
    }
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setPronto(true);
      }
    });
    // Se a sessão de recovery já foi processada antes deste efeito montar
    // (corrida com o parse do hash pelo SDK), confirma direto.
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setPronto(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (senha.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres");
      return;
    }
    if (senha !== confirmacao) {
      setError("As senhas não coincidem");
      return;
    }
    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password: senha });
      if (updateError) throw updateError;
      setSucesso(true);
      setTimeout(() => setLocation("/dashboard"), 2000);
    } catch (err: any) {
      setError(err.message || "Erro ao redefinir senha");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src={logoImg} alt="LaudoLegal" className="h-12" />
        </div>
        <Card className="p-8 border-none shadow-2xl">
          {sucesso ? (
            <div className="text-center space-y-4" data-testid="text-sucesso">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto" />
              <h1 className="text-xl font-bold text-slate-900">Senha redefinida!</h1>
              <p className="text-muted-foreground text-sm">Redirecionando...</p>
            </div>
          ) : linkInvalido ? (
            <div className="text-center space-y-4" data-testid="text-link-invalido">
              <h1 className="text-xl font-bold text-slate-900">Link inválido ou expirado</h1>
              <p className="text-muted-foreground text-sm">
                Este link de redefinição não é mais válido. Solicite um novo.
              </p>
              <a href="/esqueci-senha" className="text-primary underline text-sm">
                Solicitar novo link
              </a>
            </div>
          ) : !pronto ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              <p className="text-muted-foreground text-sm mt-4">Validando link...</p>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <h1 className="text-2xl font-bold text-slate-900">Defina sua nova senha</h1>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="form-redefinir-senha">
                <div className="space-y-2">
                  <Label htmlFor="senha">Nova senha</Label>
                  <div className="relative">
                    <Input
                      id="senha"
                      type={showPassword ? "text" : "password"}
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      required
                      className="pr-10"
                      data-testid="input-senha"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmacao">Confirmar senha</Label>
                  <Input
                    id="confirmacao"
                    type={showPassword ? "text" : "password"}
                    value={confirmacao}
                    onChange={(e) => setConfirmacao(e.target.value)}
                    required
                    data-testid="input-confirmacao"
                  />
                </div>
                {error && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                    {error}
                  </div>
                )}
                <Button type="submit" className="w-full h-11" disabled={loading} data-testid="button-redefinir">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Redefinir senha
                </Button>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
